import type { AxiosProgressEvent, AxiosRequestConfig, AxiosResponse } from 'axios'
import { http } from './client'
import type { ApiResponse } from './types'

export interface UploadOptions {
  /** Field name in the multipart form. Default `'file'`. */
  fieldName?: string
  /** Extra fields appended to the FormData. */
  fields?: Record<string, string | Blob>
  /** Progress callback (0–100). */
  onProgress?: (percent: number) => void
  /** Pass through to Axios — `signal`, custom `headers`, etc. */
  config?: Omit<AxiosRequestConfig, 'onUploadProgress'>
  /** Per-call timeout override (ms). Defaults to no client timeout for uploads. */
  timeout?: number
}

/**
 * Upload a single file to the given endpoint with progress reporting.
 * Defaults to no client-side timeout — large uploads must rely on server limits.
 */
export function uploadFile<T = unknown>(
  url: string,
  file: File | Blob,
  options: UploadOptions = {},
): Promise<AxiosResponse<ApiResponse<T>>> {
  const { fieldName = 'file', fields = {}, onProgress, config = {}, timeout = 0 } = options

  const form = new FormData()
  form.append(fieldName, file)
  for (const [k, v] of Object.entries(fields)) form.append(k, v)

  return http.post<ApiResponse<T>>(url, form, {
    ...config,
    timeout,
    headers: {
      ...config.headers,
      'Content-Type': 'multipart/form-data',
    },
    onUploadProgress: (event: AxiosProgressEvent) => {
      if (!onProgress || !event.total) return
      onProgress(Math.round((event.loaded * 100) / event.total))
    },
  })
}
